import React, { useState } from 'react';
import Button from './Button';
import { addClassPoints } from '../../utils/firebase';

interface AddClassPointsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  schoolId: string;
  classId: string;
  currentClassPoints: number;
  teacherId: string;
  teacherName: string;
  onSuccess: () => void;
}

const AddClassPointsDialog: React.FC<AddClassPointsDialogProps> = ({
  isOpen,
  onClose,
  schoolId,
  classId,
  currentClassPoints,
  teacherId,
  teacherName,
  onSuccess,
}) => {
  const [type, setType] = useState<'add' | 'subtract'>('add');
  const [amountStr, setAmountStr] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amount = parseInt(amountStr, 10);
  const isValidAmount = !isNaN(amount) && amount >= 1;
  const subtractExceeds = type === 'subtract' && isValidAmount && amount > currentClassPoints;
  const canSubmit = isValidAmount && !subtractExceeds && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError(null);
    setIsSubmitting(true);
    try {
      const delta = type === 'add' ? amount : -amount;
      await addClassPoints(schoolId, classId, delta, {
        reason: reason.trim() || (type === 'add' ? '학급 포인트 지급' : '학급 포인트 차감'),
        teacherId,
        teacherName,
      });
      setAmountStr('');
      setReason('');
      onSuccess();
      onClose();
    } catch (err) {
      console.error('학급 포인트 지급/차감 오류:', err);
      setError('처리 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setAmountStr('');
      setReason('');
      setError(null);
      setType('add');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }}
      onClick={handleClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-class-points-title"
        style={{
          width: '100%',
          maxWidth: '400px',
          margin: 16,
          padding: '24px',
          borderRadius: '16px',
          backgroundColor: 'var(--Color-background-normal, #fff)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--gap-level-4, 16px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="add-class-points-title"
          style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: 600,
            color: 'var(--Color-text-strong, #0D0D0D)',
            fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
          }}
        >
          학급 포인트 지급/차감
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span
            style={{
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--Color-text-normal, #161616)',
            }}
          >
            구분
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setType('add')}
              style={{
                flex: 1,
                padding: '10px 16px',
                borderRadius: '8px',
                border: `1.2px solid ${type === 'add' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-line-normal, #F0F0F0)'}`,
                background: type === 'add' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                color: type === 'add' ? '#fff' : 'var(--Color-text-normal, #161616)',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
              }}
            >
              지급
            </button>
            <button
              type="button"
              onClick={() => setType('subtract')}
              style={{
                flex: 1,
                padding: '10px 16px',
                borderRadius: '8px',
                border: `1.2px solid ${type === 'subtract' ? 'var(--Color-status-negative, #FF3838)' : 'var(--Color-line-normal, #F0F0F0)'}`,
                background: type === 'subtract' ? 'var(--Color-status-negative, #FF3838)' : 'transparent',
                color: type === 'subtract' ? '#fff' : 'var(--Color-text-normal, #161616)',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-primary, Pretendard), sans-serif',
              }}
            >
              차감
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label
            htmlFor="class-points-amount"
            style={{
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--Color-text-normal, #161616)',
            }}
          >
            포인트 (현재 학급 포인트: {currentClassPoints.toLocaleString()}pt)
          </label>
          <input
            id="class-points-amount"
            type="number"
            min={1}
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="0"
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
              fontSize: '14px',
              fontFamily: 'Pretendard, sans-serif',
              boxSizing: 'border-box',
              outline: 'none',
            }}
          />
          {type === 'subtract' && subtractExceeds && (
            <span style={{ fontSize: '13px', color: 'var(--Color-status-negative, #FF3838)' }}>
              현재 학급 포인트보다 많이 차감할 수 없습니다.
            </span>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label
            htmlFor="class-points-reason"
            style={{
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--Color-text-normal, #161616)',
            }}
          >
            사유 (선택)
          </label>
          <input
            id="class-points-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="예: 단체 활동 참여 보상"
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1.2px solid var(--Color-line-normal, #F0F0F0)',
              fontSize: '14px',
              fontFamily: 'Pretendard, sans-serif',
              boxSizing: 'border-box',
              outline: 'none',
            }}
          />
        </div>

        {error && (
          <div
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              backgroundColor: 'rgba(255, 56, 56, 0.1)',
              color: 'var(--Color-status-negative, #FF3838)',
              fontSize: '13px',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
          <Button variant="cancel" onClick={handleClose} disabled={isSubmitting}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isSubmitting ? '처리 중...' : '확인'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddClassPointsDialog;
